import type { ReactNode } from 'react';

import { useFocusable } from '../focus';
import styles from './Button.module.css';
import { cx } from './cx';
import { Spinner } from './Spinner';

export interface ButtonProps {
  children: ReactNode;
  icon?: ReactNode;
  /** `default` = dark surface, `primary` = pink (Figma TV/Button). */
  variant?: 'default' | 'primary';
  /** Square icon-only button (80×80). */
  square?: boolean;
  focusKey?: string;
  disabled?: boolean;
  /** A request is in flight: the icon slot turns into a spinner. The button stays focusable. */
  loading?: boolean;
  onSelect?: () => void;
  onFocus?: () => void;
  /**
   * Explicit D-pad wiring (UX-312/UX-346). Return `false` to consume the press; anything else
   * lets norigin do its spatial move. Needed wherever geometry cannot express the intent — a
   * header button sitting above a full-width list is never "up" from any row norigin will pick.
   */
  onArrowPress?: (direction: string) => boolean;
  className?: string;
  'data-testid'?: string;
}

export function Button({
  children,
  icon,
  variant = 'default',
  square = false,
  focusKey,
  disabled = false,
  loading = false,
  onSelect,
  onFocus,
  onArrowPress,
  className,
  ...rest
}: ButtonProps) {
  const {
    ref,
    focused,
    focusKey: key,
  } = useFocusable<HTMLDivElement>({
    focusKey,
    focusable: !disabled,
    onEnterPress: () => onSelect?.(),
    onFocus: () => onFocus?.(),
    onArrowPress: (direction) => onArrowPress?.(direction) ?? true,
  });
  return (
    <div
      ref={ref}
      role="button"
      aria-disabled={disabled || undefined}
      aria-busy={loading || undefined}
      data-focus-key={key}
      data-focused={focused || undefined}
      data-testid={rest['data-testid']}
      className={cx(
        styles.button,
        variant === 'primary' && styles.primary,
        square && styles.square,
        focused && styles.focused,
        disabled && styles.disabled,
        className,
      )}
    >
      {loading ? (
        <span className={styles.icon}>
          <Spinner size={32} />
        </span>
      ) : icon ? (
        <span className={styles.icon}>{icon}</span>
      ) : null}
      {square ? null : <span className={styles.label}>{children}</span>}
    </div>
  );
}
